import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../LanguageContext";
import {
	applyLetterVariant,
	pickVariant,
	SETTLED,
	variantKey,
} from "../../lib/letterSets";
import { afterLoadIdle, clamp, dockDistance } from "../../lib/motion";

const SETTLED_ROLE = { ...SETTLED, weight: 700 };
const SPACE = "\u00a0";
const GLITCH_EVERY_MS = 3000;
const GLITCH_BURST_MS = 1100;
const GLITCH_TICK_MS = 90;
const FIRST_GLITCH_MS = 500;
const KEEP_SETTLE_FROM = 0.78;

const GLITCH_COLORS = [
	"#ea4e2c",
	"#e23722",
	"#00e5ff",
	"#ff2bd6",
	"#39ff14",
	"#ffe600",
	"#4d7cff",
	"#ffffff",
	"#7cf5ff",
	"#ff6b4a",
];

function pickGlitchColor(exclude?: string) {
	const pool = exclude
		? GLITCH_COLORS.filter((color) => color !== exclude)
		: GLITCH_COLORS;
	return pool[Math.floor(Math.random() * pool.length)];
}

function roleChar(letter: string) {
	if (letter === " ") return SPACE;
	return letter.toLowerCase();
}

export default function Role() {
	const { text } = useLanguage();
	const location = useLocation();
	const rootRef = useRef<HTMLParagraphElement>(null);
	const charWraps = useRef<Array<HTMLSpanElement | null>>([]);
	const charsRef = useRef<Array<HTMLSpanElement | null>>([]);
	const keysRef = useRef<string[]>([]);
	const glitchActive = useRef<number[]>([]);
	const glitchInterval = useRef(0);
	const glitchBurst = useRef(0);
	const glitchTick = useRef(0);
	const glitchFirst = useRef(0);
	const idleGlitch = useRef(false);
	const scrollKeepGlitch = useRef(false);
	const target = text.site.role;
	const isHome = location.pathname === "/";
	const [live, setLive] = useState(
		() => typeof window !== "undefined" && (!isHome || window.scrollY > 0),
	);

	useEffect(() => {
		if (!isHome || live) return;

		const enable = () => setLive(true);
		const stopIdle = afterLoadIdle(enable, 1800);
		window.addEventListener("scroll", enable, { once: true, passive: true });
		window.addEventListener("pointerdown", enable, { once: true });

		return () => {
			stopIdle();
			window.removeEventListener("scroll", enable);
			window.removeEventListener("pointerdown", enable);
		};
	}, [isHome, live]);

	useLayoutEffect(() => {
		if (!live) return;

		keysRef.current = target
			.split("")
			.map((letter) => `${letter}:700:normal:normal:normal`);
		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		let frame = 0;

		const letterIndexes = () => {
			const indexes: number[] = [];
			for (let i = 0; i < target.length; i += 1) {
				if (target[i] !== " " && charsRef.current[i]) indexes.push(i);
			}
			return indexes;
		};

		const ghostParts = (wrap: HTMLElement | null) => ({
			ghostA: wrap?.querySelector<HTMLElement>(".header-role-ghost-a") || null,
			ghostB: wrap?.querySelector<HTMLElement>(".header-role-ghost-b") || null,
		});

		const syncGlyph = (index: number, char: string) => {
			const wrap = charWraps.current[index];
			const live = charsRef.current[index];
			const { ghostA, ghostB } = ghostParts(wrap);
			if (live) live.textContent = char;
			if (ghostA) ghostA.textContent = char;
			if (ghostB) ghostB.textContent = char;
		};

		const applyVariantAt = (
			index: number,
			variant: Parameters<typeof applyLetterVariant>[1],
		) => {
			const live = charsRef.current[index];
			if (!live) return;
			applyLetterVariant(live, variant);
			syncGlyph(index, variant.char);
			const { ghostA, ghostB } = ghostParts(charWraps.current[index]);
			for (const ghost of [ghostA, ghostB]) {
				if (!ghost) continue;
				ghost.style.fontWeight = String(variant.weight);
				ghost.style.fontStyle = variant.style;
				ghost.style.fontVariantCaps = variant.caps;
				ghost.style.fontStretch = variant.stretch;
			}
		};

		const clearLetterGlitch = (index: number) => {
			const wrap = charWraps.current[index];
			if (!wrap) return;
			wrap.classList.remove("is-glitch");
			wrap.style.removeProperty("--glitch-a");
			wrap.style.removeProperty("--glitch-b");
			const { ghostA, ghostB } = ghostParts(wrap);
			if (ghostA) ghostA.style.cssText = "";
			if (ghostB) ghostB.style.cssText = "";
		};

		const clearGlitchBurst = () => {
			window.clearTimeout(glitchBurst.current);
			window.clearInterval(glitchTick.current);
			glitchBurst.current = 0;
			glitchTick.current = 0;
			glitchActive.current.forEach(clearLetterGlitch);
			glitchActive.current = [];
		};

		const stopIdleGlitch = () => {
			window.clearTimeout(glitchFirst.current);
			window.clearInterval(glitchInterval.current);
			glitchFirst.current = 0;
			glitchInterval.current = 0;
			idleGlitch.current = false;
			if (!scrollKeepGlitch.current) clearGlitchBurst();
		};

		const settleLetter = (index: number, char: string) => {
			applyVariantAt(index, { char, ...SETTLED_ROLE });
			const live = charsRef.current[index];
			if (live) live.style.paddingInline = "";
			keysRef.current[index] = `${char}:700:normal:normal:normal`;
			clearLetterGlitch(index);
		};

		const settleIdle = () => {
			for (let i = 0; i < target.length; i += 1) {
				if (!charsRef.current[i]) continue;
				settleLetter(i, target[i] === " " ? SPACE : target[i]);
			}
		};

		const settleLower = () => {
			for (let i = 0; i < target.length; i += 1) {
				if (!charsRef.current[i]) continue;
				settleLetter(i, roleChar(target[i]));
			}
		};

		const scrambleGlitchLetters = () => {
			glitchActive.current.forEach((index) => {
				const variant = pickVariant(
					target[index],
					keysRef.current[index] || "",
				);
				applyVariantAt(index, variant);
				keysRef.current[index] = variantKey(variant);
			});
		};

		const armGlitch = (indexes: number[]) => {
			indexes.forEach((index) => {
				const wrap = charWraps.current[index];
				if (!wrap) return;
				const colorA = pickGlitchColor();
				const colorB = pickGlitchColor(colorA);
				wrap.style.setProperty("--glitch-a", colorA);
				wrap.style.setProperty("--glitch-b", colorB);
				wrap.classList.add("is-glitch");
			});
			glitchActive.current = indexes;
			scrambleGlitchLetters();
			window.clearInterval(glitchTick.current);
			glitchTick.current = window.setInterval(
				scrambleGlitchLetters,
				GLITCH_TICK_MS,
			);
		};

		const startIdleBurst = () => {
			if (scrollKeepGlitch.current) return;
			clearGlitchBurst();
			const unlocked = letterIndexes();
			if (!unlocked.length) return;

			const count = Math.max(
				1,
				Math.floor(Math.random() * Math.min(unlocked.length, 4)) + 1,
			);
			const pool = [...unlocked];
			for (let i = pool.length - 1; i > 0; i -= 1) {
				const j = Math.floor(Math.random() * (i + 1));
				[pool[i], pool[j]] = [pool[j], pool[i]];
			}
			armGlitch(pool.slice(0, Math.min(count, pool.length)));
			glitchBurst.current = window.setTimeout(
				clearGlitchBurst,
				GLITCH_BURST_MS,
			);
		};

		const startIdleGlitch = () => {
			if (reduced || idleGlitch.current) return;
			idleGlitch.current = true;
			settleIdle();
			glitchFirst.current = window.setTimeout(() => {
				startIdleBurst();
				glitchInterval.current = window.setInterval(
					startIdleBurst,
					GLITCH_EVERY_MS,
				);
			}, FIRST_GLITCH_MS);
		};

		const startScrollGlitch = () => {
			if (reduced || scrollKeepGlitch.current) return;
			scrollKeepGlitch.current = true;
			window.clearTimeout(glitchBurst.current);
			glitchBurst.current = 0;
			armGlitch(letterIndexes());
		};

		const stopScrollGlitch = () => {
			if (!scrollKeepGlitch.current) return;
			scrollKeepGlitch.current = false;
			clearGlitchBurst();
		};

		const syncPhase = (progress: number) => {
			const root = rootRef.current;
			if (!root) return;
			const intro = isHome && progress <= 0;
			root.classList.toggle("is-intro", intro);
			root.classList.toggle("is-scrolling", isHome && progress > 0);
		};

		const applyLetters = (progress: number) => {
			if (reduced || !isHome) {
				stopIdleGlitch();
				stopScrollGlitch();
				settleLower();
				return;
			}

			if (progress <= 0) {
				stopScrollGlitch();
				startIdleGlitch();
				return;
			}

			if (idleGlitch.current) stopIdleGlitch();

			if (progress < KEEP_SETTLE_FROM) {
				startScrollGlitch();
				return;
			}

			stopScrollGlitch();
			settleLower();
		};

		const render = () => {
			const progress = isHome ? clamp(window.scrollY / dockDistance()) : 1;
			syncPhase(progress);
			applyLetters(progress);
		};

		const schedule = () => {
			if (frame) return;
			frame = window.requestAnimationFrame(() => {
				frame = 0;
				render();
			});
		};

		settleLower();
		render();
		if (isHome) {
			window.addEventListener("scroll", schedule, { passive: true });
			window.addEventListener("resize", schedule);
		}
		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener("scroll", schedule);
			window.removeEventListener("resize", schedule);
			stopIdleGlitch();
			stopScrollGlitch();
		};
	}, [target, isHome, live]);

	return (
		<p
			ref={rootRef}
			className={`header-role${isHome ? " is-intro" : ""}`}
			aria-label={target}
		>
			{target.split("").map((letter, index) => (
				<span
					key={`${letter}-${index}`}
					className={`header-role-char${letter === " " ? " is-space" : ""}`}
					ref={(node) => {
						charWraps.current[index] = node;
					}}
				>
					<span
						className="header-role-ghost header-role-ghost-a"
						aria-hidden="true"
					>
						{letter}
					</span>
					<span
						className="header-role-ghost header-role-ghost-b"
						aria-hidden="true"
					>
						{letter}
					</span>
					<span
						className="header-role-live"
						ref={(node) => {
							charsRef.current[index] = node;
						}}
					>
						{letter}
					</span>
				</span>
			))}
		</p>
	);
}
